import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Printer } from "lucide-react";
import { API, inputCls, selectCls, dstr, btnGhost, btnRed } from "./shared";

export const DeliveryNotes = ({ onPrint }) => {
  const [notes, setNotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({ invoice_id: "", offer_id: "", customer_name: "", delivery_date: new Date().toISOString().slice(0, 10), note: "" });
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/delivery-notes`, { withCredentials: true }).then(({ data }) => setNotes(data)).catch(() => {});
    axios.get(`${API}/invoices`, { withCredentials: true }).then(({ data }) => setInvoices(data)).catch(() => {});
    axios.get(`${API}/offers`, { withCredentials: true }).then(({ data }) => setOffers(data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async () => {
    if (!form.invoice_id && !form.offer_id && !form.customer_name.trim()) {
      toast.error("Bitte Rechnung/Angebot wählen oder Kundenname angeben.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/delivery-notes`, form, { withCredentials: true });
      toast.success(`Lieferschein ${data.number} erstellt.`);
      setShowForm(false);
      setForm({ invoice_id: "", offer_id: "", customer_name: "", delivery_date: new Date().toISOString().slice(0, 10), note: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erstellen fehlgeschlagen.");
    }
  };

  const update = async (id, patch) => {
    try {
      const { data } = await axios.patch(`${API}/delivery-notes/${id}`, patch, { withCredentials: true });
      setNotes((ns) => ns.map((n) => (n.id === id ? data : n)));
    } catch {
      toast.error("Aktualisieren fehlgeschlagen.");
    }
  };

  const del = async (id) => {
    await axios.delete(`${API}/delivery-notes/${id}`, { withCredentials: true }).catch(() => toast.error("Löschen fehlgeschlagen."));
    setNotes((ns) => ns.filter((n) => n.id !== id));
  };

  return (
    <div data-testid="delivery-view">
      <button data-testid="delivery-new" onClick={() => setShowForm(!showForm)} className={`${btnRed} mb-6`}>
        <Plus size={15} /> Neuer Lieferschein
      </button>

      {showForm && (
        <div className="bg-[#1e2226] border border-white/10 rounded-2xl p-6 mb-6" data-testid="delivery-form">
          <p className="font-display text-white/40 text-xs uppercase tracking-[0.2em] mb-4">Lieferschein erstellen – Positionen werden aus Rechnung oder Angebot übernommen</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <select data-testid="delivery-from-invoice" value={form.invoice_id} onChange={(e) => setForm({ ...form, invoice_id: e.target.value, offer_id: "" })} className={selectCls}>
              <option value="">Aus Rechnung übernehmen …</option>
              {invoices.map((i) => <option key={i.id} value={i.id}>{i.number} · {i.customer_name}</option>)}
            </select>
            <select data-testid="delivery-from-offer" value={form.offer_id} onChange={(e) => setForm({ ...form, offer_id: e.target.value, invoice_id: "" })} className={selectCls}>
              <option value="">Aus Angebot übernehmen …</option>
              {offers.map((o) => <option key={o.id} value={o.id}>{o.number} · {o.customer_name}</option>)}
            </select>
            {!form.invoice_id && !form.offer_id && (
              <input data-testid="delivery-customer" value={form.customer_name} onChange={set("customer_name")} placeholder="Kundenname *" className={inputCls} />
            )}
            <div>
              <label className="text-white/40 text-xs">Lieferdatum</label>
              <input data-testid="delivery-date" type="date" value={form.delivery_date} onChange={set("delivery_date")} className={`${inputCls} mt-1`} />
            </div>
          </div>
          <textarea data-testid="delivery-note" rows={2} value={form.note} onChange={set("note")} placeholder="Hinweis (optional), z. B. Ablageort, Restmenge …" className={`${inputCls} resize-none mt-4`} />
          <button data-testid="delivery-create" onClick={create} className={`${btnRed} mt-4`}>
            <Plus size={15} /> Lieferschein erstellen
          </button>
        </div>
      )}

      {notes.length === 0 ? (
        <div data-testid="delivery-empty" className="bg-[#1e2226] border border-white/10 rounded-2xl p-14 text-center text-white/50">
          Noch keine Lieferscheine – erstelle oben den ersten aus einer Rechnung oder einem Angebot.
        </div>
      ) : (
        <div data-testid="delivery-list" className="space-y-3">
          {notes.map((n) => (
            <div key={n.id} data-testid={`delivery-${n.id}`} className="bg-[#1e2226] border border-white/10 rounded-2xl p-5">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[220px]">
                  <p className="font-display font-semibold">{n.number} · {n.customer_name}</p>
                  <p className="text-white/45 text-xs mt-1">
                    {n.items.length} {n.items.length === 1 ? "Position" : "Positionen"}
                    {n.invoice_id && ` · zur Rechnung ${invoices.find((i) => i.id === n.invoice_id)?.number || ""}`}
                    {n.offer_id && ` · zum Angebot ${offers.find((o) => o.id === n.offer_id)?.number || ""}`}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-white/40 text-xs">Lieferdatum</label>
                  <input
                    data-testid={`delivery-date-${n.id}`}
                    type="date"
                    value={n.delivery_date}
                    onChange={(e) => update(n.id, { delivery_date: e.target.value })}
                    className="bg-white/5 border border-white/15 rounded-lg px-3 py-1.5 text-white text-xs focus:outline-none focus:border-[#e2001a] [color-scheme:dark]"
                  />
                </div>
                <div className="flex gap-2">
                  <button data-testid={`delivery-print-${n.id}`} onClick={() => onPrint(n)} className={btnGhost}>
                    <Printer size={14} /> Drucken
                  </button>
                  <button data-testid={`delivery-delete-${n.id}`} onClick={() => del(n.id)} className="text-white/30 hover:text-[#ff4d5e] transition-colors p-2" aria-label="Lieferschein löschen">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
              <input
                data-testid={`delivery-note-edit-${n.id}`}
                value={n.note}
                onChange={(e) => update(n.id, { note: e.target.value })}
                placeholder="Hinweis (optional) …"
                className={`${inputCls} !py-2 !text-xs mt-3`}
              />
            </div>
          ))}
        </div>
      )}
      <p className="text-white/30 text-xs mt-4">Erstellt am {dstr(new Date().toISOString())}</p>
    </div>
  );
};
